import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static targets = ['tablist', 'tab', 'accordionButton', 'panel'];
    static values = {
        breakpoint: {
            type: Number,
            default: 768,
        },
    };

    connect() {
        this.setupMediaQuery();
        this.setupKeyboardNavigation();
    }

    disconnect() {
        if (this.mediaQuery) {
            if (this.mediaQuery.removeEventListener) { // Modern browsers
                this.mediaQuery.removeEventListener('change', this.handleMediaChange);
            } else if (this.mediaQuery.removeListener) { // Legacy browsers (Safari < 14)
                this.mediaQuery.removeListener(this.handleMediaChange);
            }
        }
    }

    setupMediaQuery() {
        this.mediaQuery = window.matchMedia(`(min-width: ${this.breakpointValue}px)`);

        // Bind the handler so we can remove it later
        this.handleMediaChange = this.onMediaChange.bind(this);

        if (this.mediaQuery.addEventListener) { // Modern browsers
            this.mediaQuery.addEventListener('change', this.handleMediaChange);
        } else if (this.mediaQuery.addListener) { // Legacy browsers (Safari < 14)
            this.mediaQuery.addListener(this.handleMediaChange);
        }

        // Set initial state
        this.isTabMode = this.mediaQuery.matches;
        this.updateAriaAttributes();
    }

    onMediaChange(event) {
        const wasTabMode = this.isTabMode;
        this.isTabMode = event.matches;

        if (wasTabMode !== this.isTabMode) {
            this.handleModeSwitch(wasTabMode);
        }
    }

    handleModeSwitch(wasTabMode) {
        if (this.isTabMode && !wasTabMode) {
            // Switching from accordion to tabs
            // Check how many panels are open
            const openPanels = this.panelTargets.filter(panel => !panel.hidden);
            const activeIndex = openPanels.length === 0 ? 0 : this.panelTargets.findIndex(panel => !panel.hidden);

            this.activateSinglePanel(activeIndex);
        }

        // Update ARIA for the new mode
        this.updateAriaAttributes();
    }

    updateAriaAttributes() {
        if (this.isTabMode) {
            this.setTabModeAria();
        } else {
            this.setAccordionModeAria();
        }
    }

    setTabModeAria() {
        this.tablistTarget.setAttribute('role', 'tablist');

        const activeIndex = this.panelTargets.findIndex(panel => !panel.hidden);

        this.tabTargets.forEach((tab, index) => {
            tab.setAttribute('role', 'tab');
            const isActive = index === activeIndex;
            tab.setAttribute('aria-selected', isActive);
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.panelTargets.forEach((panel, index) => {
            panel.setAttribute('role', 'tabpanel');
            const tabId = `tab-${index}`;
            panel.setAttribute('aria-labelledby', tabId);
        });

        this.accordionButtonTargets.forEach(button => {
            button.removeAttribute('aria-expanded');
        });
    }

    setAccordionModeAria() {
        this.tablistTarget.removeAttribute('role');

        const activeIndex = this.panelTargets.findIndex(panel => !panel.hidden);

        this.tabTargets.forEach(tab => {
            tab.removeAttribute('role');
            tab.removeAttribute('aria-selected');
        });

        this.accordionButtonTargets.forEach((button, index) => {
            const isExpanded = index === activeIndex;
            button.setAttribute('aria-expanded', isExpanded);
        });

        this.panelTargets.forEach((panel, index) => {
            panel.setAttribute('role', 'region');
            const accordionBtnId = `accordion-btn-${index}`;
            panel.setAttribute('aria-labelledby', accordionBtnId);
        });
    }

    activateSinglePanel(index) {
        // Close all panels
        this.panelTargets.forEach((panel) => {
            panel.hidden = true;
        });

        // Open the selected panel
        this.panelTargets[index].hidden = false;

        // Update ARIA attributes based on current mode
        if (this.isTabMode) {
            this.tabTargets.forEach((tab, i) => {
                const isActive = i === index;
                tab.setAttribute('aria-selected', isActive);
                tab.setAttribute('tabindex', isActive ? '0' : '-1');
            });
        } else {
            this.accordionButtonTargets.forEach((button, i) => {
                button.setAttribute('aria-expanded', i === index);
            });
        }
    }

    toggleAccordionPanel(index) {
        const panel = this.panelTargets[index];
        const button = this.accordionButtonTargets[index];
        const isExpanded = !panel.hidden;

        // Toggle this panel
        panel.hidden = isExpanded;
        button.setAttribute('aria-expanded', !isExpanded);
    }

    selectTab(event) {
        if (!this.isTabMode) {
            return; // Ignore in accordion mode
        }

        const clickedTab = event.currentTarget;
        const index = parseInt(clickedTab.dataset.index);
        this.activateSinglePanel(index);
    }

    toggleAccordion(event) {
        if (this.isTabMode) {
            return; // Ignore in tab mode
        }

        const button = event.currentTarget;
        const index = parseInt(button.dataset.index);
        this.toggleAccordionPanel(index);
    }

    setupKeyboardNavigation() {
        this.tabTargets.forEach(tab => {
            tab.addEventListener('keydown', this.handleTabKeydown.bind(this));
        });

        this.accordionButtonTargets.forEach(button => {
            button.addEventListener('keydown', this.handleAccordionKeydown.bind(this));
        });
    }

    handleTabKeydown(event) {
        if (!this.isTabMode) {
            return; // Only handle in tab mode
        }

        const currentIndex = parseInt(event.currentTarget.dataset.index);
        const nextIndex = (currentIndex + 1) % this.tabTargets.length;
        const prevIndex = currentIndex - 1 < 0 ? this.tabTargets.length - 1 : currentIndex - 1;
        const lastIndex = this.tabTargets.length - 1;

        switch (event.key) {
            case 'Home':
                event.preventDefault();
                this.activateSinglePanel(0);
                this.tabTargets[0].focus();
                break;

            case 'End':
                event.preventDefault();
                this.activateSinglePanel(lastIndex);
                this.tabTargets[lastIndex].focus();
                break;

            case 'ArrowRight':
                event.preventDefault();
                this.activateSinglePanel(nextIndex);
                this.tabTargets[nextIndex].focus();
                break;

            case 'ArrowLeft':
                event.preventDefault();
                this.activateSinglePanel(prevIndex);
                this.tabTargets[prevIndex].focus();
                break;
        }
    }

    handleAccordionKeydown(event) {
        if (this.isTabMode) {
            return; // Only handle in accordion mode
        }

        const currentIndex = parseInt(event.currentTarget.dataset.index);
        const nextIndex = (currentIndex + 1) % this.accordionButtonTargets.length;
        const prevIndex = currentIndex - 1 < 0 ? this.accordionButtonTargets.length - 1 : currentIndex - 1;
        const lastIndex = this.accordionButtonTargets.length - 1;

        switch (event.key) {
            case 'Home':
                event.preventDefault();
                this.accordionButtonTargets[0].focus();
                break;

            case 'End':
                event.preventDefault();
                this.accordionButtonTargets[lastIndex].focus();
                break;

            case 'ArrowDown':
                event.preventDefault();
                this.accordionButtonTargets[nextIndex].focus();
                break;

            case 'ArrowUp':
                event.preventDefault();
                this.accordionButtonTargets[prevIndex].focus();
                break;
        }
    }
}
